<template>
    <silent-box
        v-if="photos.length > 0 && gallery.some((item) => item && item.src)"
        :gallery="gallery.filter((item) => item && item.src)"
        :class="compact ? 'silentbox-gallery-compact' : ''"
        @silentbox-overlay-opened="togglePageScrollOnGallery"
        @silentbox-overlay-hidden="togglePageScrollOnGallery"
    >
        <template v-slot:silentbox-item="{ silentboxItem }">
            <slot name="item-actions" :photo="silentboxItem.photo"></slot>
            <AuthenticatedPhoto
                v-if="silentboxItem.photo"
                :url="silentboxItem.photo.url"
                :loading="silentboxItem.loading"
                @loading-change="silentboxItem.loading = $event"
            />
        </template>
    </silent-box>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import AuthenticatedPhoto from "@/views/shared/AuthenticatedPhoto.vue";
import { PortalNoteMedia } from "@/views/notes/model";
import {
    SilentboxGalleryItem,
    buildSilentboxGallery,
    loadSilentboxGallerySources,
    togglePageScrollOnGallery,
} from "@/views/shared/mediaGallery";

export default Vue.extend({
    name: "SilentboxMediaGallery",
    components: { AuthenticatedPhoto },
    props: {
        photos: {
            type: Array as PropType<PortalNoteMedia[]>,
            required: true,
        },
        compact: {
            type: Boolean,
            default: false,
        },
    },
    data(): { gallery: SilentboxGalleryItem[] } {
        return { gallery: [] };
    },
    watch: {
        photos: {
            handler() {
                this.initGallery();
            },
            deep: true,
        },
    },
    mounted() {
        this.initGallery();
    },
    methods: {
        togglePageScrollOnGallery,
        initGallery(): void {
            this.gallery = buildSilentboxGallery(this.photos);
            loadSilentboxGallerySources(this.$services.api, this.gallery, (index, item) => {
                this.$set(this.gallery, index, item);
            });
        },
    },
});
</script>

<style scoped lang="scss">
@use "src/scss/mixins";
@use "src/scss/variables";

#silentbox-gallery {
    display: flex;
    flex-wrap: wrap;
    gap: 20px 10px;
    margin-top: 20px;

    @include mixins.bp-down(variables.$xs) {
        justify-content: space-between;
    }
}

.silentbox-gallery-compact#silentbox-gallery {
    gap: 8px;
    margin-top: 10px;
}

::v-deep .silentbox-item {
    position: relative;
    background-color: #e2e4e6;
    height: 300px;
    min-width: 100px;
    border-radius: 2px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 2px 6px rgba(0, 0, 0, 0.06);

    @supports (-webkit-hyphens: none) {
        flex: 0 0 calc(50% - 5px);
        width: calc(50% - 5px);
    }

    &:has(.spinner) {
        min-width: 250px;
        min-height: 250px;
    }

    @include mixins.bp-down(variables.$xs) {
        height: auto;
        max-height: 80vh;
        flex: 1 1 auto;

        @supports (-webkit-hyphens: none) {
            flex: 1 1 100%;
            width: 100%;
        }
    }

    img {
        object-fit: cover;
        border-radius: 2px;
        width: 100%;
        height: 100%;
    }
}

.silentbox-gallery-compact ::v-deep .silentbox-item {
    height: 40px;
    min-width: 60px;
    flex: 0 0 60px;
    width: 60px;

    &:has(.spinner) {
        min-width: 60px;
        min-height: 50px;
    }
}
</style>
